import { DownloadOutlined, ExclamationCircleOutlined, InfoCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, Col, Dropdown, Form, Grid, Modal, Row, Space, message } from 'antd';
import type { ItemType } from 'antd/lib/menu/hooks/useItems';
import { Formik } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import getApolloErrors from '../../../../server/utils/getApolloErrors';
import * as permissionKind from '../../../../shared/permissions';
import { type LanguagePackSpecsFragment } from '../../../api/fragments/LanguagePackSpecs';
import {
    DeleteLanguagePackDocument,
    type DeleteLanguagePackMutation,
    type DeleteLanguagePackMutationVariables,
} from '../../../api/mutations/deleteLanguagePack';
import {
    UpdateLanguagePackDocument,
    type UpdateLanguagePackMutation,
    type UpdateLanguagePackMutationVariables,
} from '../../../api/mutations/updateLanguagePack';
import { LanguageOrientation, type LanguagePackSettings } from '../../../api/types';
import { useAccountContext } from '../../../components/contexts/AccountContextManager';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import InputField from '../../../components/fields/InputField';
import SelectField from '../../../components/fields/SelectField';
import CollapsibleWrapper, { Panel } from '../../../components/wrappers/CollapsibleWrapper';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import { useThemeComponents } from '../../../themes';
import { type SelectProps } from '../../../themes/context';
import { exportLanguagePack } from '../../../utilities/export';
import hasPermissions from '../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../utilities/useFormattedSimpleVersioning';
import useHandleError from '../../../utilities/useHandleError';
import useLanguageList from '../../../utilities/useLanguageList';
import useLanguagePackValidator from '../LanguagePackListPage/useLanguagePackValidator';
import useLanguagePackModal from './LanguagePackModal';
import LanguagePackTranslations from './LanguagePackTranslations';

export type LanguagePackInnerPageProps = { language: LanguagePackSpecsFragment };

type FormValues = LanguagePackSettings;

const filterOption: SelectProps['filterOption'] = (
    input: string,
    option?: { label: string | number | React.ReactNode }
) => option?.label.toString().toLowerCase().includes(input.toLowerCase());

const LanguagePackInnerPage = ({ language }: LanguagePackInnerPageProps) => {
    const { data, loading } = useLanguageList();
    const { t } = useTranslation(['languagePackDetails']);
    const { token } = useAccountContext();
    const navigate = useNavigate();
    const { FormFields } = useThemeComponents();
    const company = useCompany(true);

    const languagePackId = language.id;
    const validate = useLanguagePackValidator();
    const screens = Grid.useBreakpoint();

    const [isExporting, setIsExporting] = useState(false);

    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: language.versioning,
        timeZone: company?.timeZone,
    });

    const { open, render } = useLanguagePackModal(language.id);
    const apolloClient = useApolloClient();

    const onSubmit = useHandleError<FormValues>(
        async values => {
            // submitting message
            message.loading({
                content: t('languagePackDetails:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            // submit update
            await apolloClient
                .mutate<UpdateLanguagePackMutation, UpdateLanguagePackMutationVariables>({
                    mutation: UpdateLanguagePackDocument,
                    variables: { id: languagePackId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            // inform about success
            message.success({
                content: t('languagePackDetails:messages.updateSuccessful'),
                key: 'primary',
            });

            // then go back to list
            navigate('/admin/system/languages');
        },
        [apolloClient, t, languagePackId, navigate]
    );

    const deleteLanguagePack = useCallback(() => {
        Modal.confirm({
            title: t('languagePackDetails:deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('languagePackDetails:deleteModal.content'),
            okText: t('languagePackDetails:deleteModal.okText'),
            okType: 'danger',
            cancelText: t('languagePackDetails:deleteModal.cancelText'),
            async onOk() {
                try {
                    // loading message
                    message.loading({
                        content: t('languagePackDetails:messages.deleteSubmitting'),
                        key: 'primary',
                        duration: 0,
                    });

                    // delete with the API
                    await apolloClient
                        .mutate<DeleteLanguagePackMutation, DeleteLanguagePackMutationVariables>({
                            mutation: DeleteLanguagePackDocument,
                            variables: { id: languagePackId },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });

                    // show success
                    message.success({
                        content: t('languagePackDetails:messages.deleteSuccessful'),
                        key: 'primary',
                    });

                    // then go back to list
                    navigate('/admin/system/languages');
                } catch (error) {
                    const apolloErrors = getApolloErrors(error);

                    if (apolloErrors?.$root) {
                        message.error(apolloErrors?.$root);
                    }
                }
            },
        });
    }, [apolloClient, t, languagePackId, navigate]);

    const initialValues = useMemo(
        (): FormValues => ({
            referenceName: language.referenceName,
            displayName: language.displayName,
            orientation: language.orientation,
            code: language.code,
            translations: language.translations,
        }),
        [language]
    );

    const onExportLanguagePack = useCallback(
        async (exportType: string) => {
            setIsExporting(true);

            try {
                message.loading({
                    content: t('languagePackDetails:messages.exporting'),
                    key: 'primary',
                    duration: 0,
                });

                await exportLanguagePack(language.id, language.displayName, token, exportType);

                // inform about success
                message.success({
                    content: t('languagePackDetails:messages.exportSuccessful'),
                    key: 'primary',
                });
            } catch (error) {
                console.error(error);
                message.error({
                    content: t('languagePackDetails:messages.exportFailed'),
                    key: 'primary',
                });
            } finally {
                setIsExporting(false);
            }
        },
        [language.displayName, language.id, t, token]
    );

    const exportMenuItems: ItemType[] = [
        {
            label: t('languagePackDetails:actions.exportLanguage.dropdown.ci'),
            key: 'ci',
            disabled: isExporting,
        },
        {
            label: t('languagePackDetails:actions.exportLanguage.dropdown.admin'),
            key: 'admin',
            disabled: isExporting,
        },
    ];

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit, setFieldValue, isValid }) => (
                <ConsolePageWithHeader
                    extra={
                        <>
                            <Button icon={<UploadOutlined />} onClick={() => open()} type="primary">
                                {t('languagePackDetails:actions.importLanguage')}
                            </Button>
                            <Dropdown
                                menu={{
                                    items: exportMenuItems,
                                    onClick: ({ key }) => onExportLanguagePack(key),
                                }}
                            >
                                <Button icon={<DownloadOutlined />} loading={isExporting} type="primary">
                                    {t('languagePackDetails:actions.exportLanguage.label')}
                                </Button>
                            </Dropdown>
                            <Button
                                key="submit"
                                disabled={!isValid}
                                form="languagePackUpdate"
                                htmlType="submit"
                                type="primary"
                            >
                                {t('languagePackDetails:actions.update')}
                            </Button>
                            {hasPermissions(language.permissions, [permissionKind.deleteLanguagePack]) ? (
                                <Button onClick={deleteLanguagePack} danger>
                                    {t('languagePackDetails:actions.delete')}
                                </Button>
                            ) : null}
                        </>
                    }
                    onBack={() => navigate('/admin/system/languages')}
                    title={
                        screens.md
                            ? t('languagePackDetails:title', { name: language.displayName })
                            : language.displayName
                    }
                >
                    <Form
                        id="languagePackUpdate"
                        layout="vertical"
                        name="languagePackUpdate"
                        onSubmitCapture={handleSubmit}
                    >
                        <>
                            {render({ onChange: newValue => setFieldValue('translations', newValue) })}
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <CollapsibleWrapper
                                    defaultActiveKey={t('languagePackDetails:cardTitles.configuration')}
                                >
                                    <Panel
                                        key={t('languagePackDetails:cardTitles.configuration')}
                                        header={t('languagePackDetails:cardTitles.configuration')}
                                    >
                                        <Row gutter={14}>
                                            <Col lg={8} xs={24}>
                                                <InputField
                                                    {...t('languagePackDetails:fields.referenceName', {
                                                        returnObjects: true,
                                                    })}
                                                    name="referenceName"
                                                    required
                                                />
                                            </Col>
                                            <Col lg={8} xs={24}>
                                                <InputField
                                                    {...t('languagePackDetails:fields.displayName', {
                                                        returnObjects: true,
                                                    })}
                                                    name="displayName"
                                                    required
                                                />
                                            </Col>
                                            <Col lg={8} xs={24}>
                                                <SelectField
                                                    showSearch
                                                    {...t('languagePackDetails:fields.code', { returnObjects: true })}
                                                    filterOption={filterOption}
                                                    loading={loading}
                                                    name="code"
                                                    options={data}
                                                    tooltip={{
                                                        title: t('languagePackDetails:fields.code.tooltip'),
                                                        icon: <InfoCircleOutlined />,
                                                    }}
                                                />
                                            </Col>
                                        </Row>
                                        <Row gutter={14}>
                                            <Col lg={8} xs={24}>
                                                <SelectField
                                                    {...t('languagePackDetails:fields.orientation', {
                                                        returnObjects: true,
                                                    })}
                                                    name="orientation"
                                                    options={[
                                                        {
                                                            value: LanguageOrientation.LeftToRight,
                                                            label: t(
                                                                'languagePackDetails:options.orientation.LeftToRight'
                                                            ),
                                                        },
                                                        {
                                                            value: LanguageOrientation.RightToLeft,
                                                            label: t(
                                                                'languagePackDetails:options.orientation.RightToLeft'
                                                            ),
                                                        },
                                                    ]}
                                                    showSearch
                                                />
                                            </Col>
                                            <Col lg={8} xs={24}>
                                                <FormFields.DisplayFieldWithUTCOffset
                                                    {...t('languagePackDetails:fields.updatedAt', {
                                                        returnObjects: true,
                                                        offset,
                                                    })}
                                                    value={updated}
                                                />
                                            </Col>
                                        </Row>
                                    </Panel>
                                </CollapsibleWrapper>

                                <LanguagePackTranslations />
                            </Space>
                        </>
                    </Form>
                </ConsolePageWithHeader>
            )}
        </Formik>
    );
};

export default LanguagePackInnerPage;
